// src/utils/tabRoutes.js
// Each main tab has its own address under the portal's base path, e.g.
//   /portal/                → Dashboard (default, 3.20.0)
//   /portal/compose         → Compose Fax (fax.send, 3.20.0)
//   /portal/outbox          → Outbox (/portal/outgoing before 3.22.4)
//   /portal/inbox           → Inbox (/portal/incoming before 3.22.4)
//   My Sessions: a pop-up from the profile menu since 3.29.3 (no tab)
//   /portal/trash           → Trash (/portal/purged-faxes before 3.22.4)
//   /portal/usage           → Usage
//   /portal/users           → Users: people, Activity log, Reminders (3.9)
//   /portal/console         → Console (Super Admins; /portal/control-panel before 3.22.4)
//   /portal/library         → Library (/portal/knowledge-base before 3.22.4)
// Before 3.9, Logs was /portal/logs and People / Activity log / Reminders
// were /portal/control-panel/users|activity|reminders: those addresses
// still open the right place (tabFromLocation below).
// Hosting sends every path to index.html, so these work on refresh and
// as shared links. The portal used to live at /portal/clinicmindrcm/;
// hosting redirects those links, and LEGACY_PREFIXES below is a safety net
// that maps them to the same tab if a redirect is ever missed.

const BASE = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');
const LEGACY_PREFIXES = ['/portal/clinicmindrcm', '/clinicmind/portal'];
// 3.22.4: the addresses follow the menu names (Outbox, Inbox, Trash,
// Console, Library); the old ones still open the same tab and are tidied to
// the new address, so bookmarks and links in older emails keep working
const OLD_SLUGS = { outgoing: 'outgoing', incoming: 'incoming', 'purged-faxes': 'purged', 'control-panel': 'control', 'knowledge-base': 'kb' };
const MOVED_FROM_CONSOLE = {
  'refused-emails': 'security', 'rejected-emails': 'security', 'blocked-senders': 'security', 'blocked-connections': 'security', 'blocked-requests': 'security',
  'costs-telnyx': 'costs', 'costs-gcp': 'costs', health: 'health',
};

// perm: the role permission that shows the tab (Roles & Access);
// superAdmin: Super Admins only
export const TABS = [
  { id: 'dashboard', slug: '',              label: 'Overview',       icon: '🏠' },
  { id: 'compose',  slug: 'compose',        label: 'Compose',        icon: '✍️', perm: 'fax.send' },
  { id: 'outgoing', slug: 'outbox',         label: 'Outbox',         icon: '📤', perm: 'tab.outgoing' },
  { id: 'incoming', slug: 'inbox',          label: 'Inbox',          icon: '📥', perm: 'tab.incoming' },
  // Their own sign-ins, for people without the Users tab (which has it)
  { id: 'purged',   slug: 'trash',          label: 'Trash',          icon: '🗑️', perm: 'fax.viewPurged' },   // "Purged Faxes" before 3.19.8
  { id: 'contacts', slug: 'contacts',       label: 'Contacts',       icon: '📇', perm: 'tab.contacts' },     // 3.25.0
  { id: 'logs',     slug: 'logs',           label: 'Logs',           icon: '📁', perm: 'tab.logs', alsoOne: ['logs.failedSent', 'logs.failedReceived', 'logs.repeatedFailures', 'logs.blockedAccess', 'logs.problemNumbers'], rail: true, railGroup: 'Activity' },   // 3.62.16: + Problem numbers
  // 3.39.0: rail = in the icon strip on the left (desktop), not the top bar,
  // under its railGroup heading (3.40.0: in SideRail's GROUP_ORDER)
  { id: 'usage',    slug: 'usage',          label: 'Usage',          icon: '👨🏻‍💻', perm: 'tab.usage', rail: true, railGroup: 'Activity' },
  { id: 'users',    slug: 'users',          label: 'Users',          icon: '🎭', perm: ['users.view', 'activity.view', 'teams.manage'], rail: true, railGroup: 'People & settings' },
  { id: 'settings', slug: 'settings',       label: 'Settings',       icon: '🛠️', perm: ['tab.settings', 'reminders.view'], rail: true, railGroup: 'People & settings' },   // every client's own settings (3.29.2)
  { id: 'control',  slug: 'console',        label: 'Console',        icon: '⚙️', superAdmin: true, rail: true, railGroup: 'Super Admin' },
  // 3.49.0: out of the Console into their own Super Admin pages, in this order
  { id: 'security', slug: 'security',       label: 'Security',       icon: '🔒', superAdmin: true, rail: true, railGroup: 'Super Admin' },
  { id: 'analytics', slug: 'analytics',     label: 'Analytics',      icon: '📊', superAdmin: true, rail: true, railGroup: 'Super Admin' },
  { id: 'costs',    slug: 'cost-analysis',  label: 'Cost Analysis',  icon: '💰', superAdmin: true, rail: true, railGroup: 'Super Admin' },
  { id: 'health',   slug: 'health',         label: 'Health',         icon: '🩺', superAdmin: true, rail: true, railGroup: 'Super Admin' },
  { id: 'kb',       slug: 'library',        label: 'Library',        icon: '📚', perm: 'tab.kb', rail: true, railGroup: 'Resources' },   // 3.40.0: was Help (the menu under your name has Help)
  // 3.68.0 (owner): the client's signed contracts; Super Admins and Master Admins only
  { id: 'documents', slug: 'documents',     label: 'Documents',      icon: '📜', masterAdmin: true, rail: true, railGroup: 'Resources' },
  // 3.43.0: the Security & compliance report (Super Admins; roles given compliance.report); 3.43.2: under Resources; 3.68.5 (owner): Library, Documents, then Compliance
  { id: 'compliance', slug: 'compliance',   label: 'Compliance',     icon: '🔐', perm: 'compliance.report', clientFlag: 'complianceReport', rail: true, railGroup: 'Resources' },
];

// Can this person open the tab? (Super Admins have every permission)
export function canOpenTab(id, { isSuperAdmin, can, flags = {}, level = '' }) {
  const t = TABS.find(x => x.id === id);
  if (!t) return false;
  if (t.superAdmin) return isSuperAdmin;
  if (t.masterAdmin) return isSuperAdmin || level === 'master_admin';   // 3.68.0
  if (t.onlyWithout && canOpenTab(t.onlyWithout, { isSuperAdmin, can, flags, level })) return false;
  if (!t.perm) return true;   // the Dashboard: everyone (3.20.0)
  if (isSuperAdmin) return true;
  // clientFlag (3.49.21): a tick on the client too (Console → Clients)
  if (t.clientFlag && !flags[t.clientFlag]) return false;
  // alsoOne (3.30.2): the tab needs at least one of its parts too
  return [].concat(t.perm).some(p => can(p)) && (!t.alsoOne || t.alsoOne.some(p => can(p)));
}
export const DEFAULT_TAB = 'dashboard';

// "Open the portal on" (Preferences): id = the saved preference, tab = the
// tab it opens. 'outbox' is new in 3.30.3; the old value 'outgoing' meant the
// Overview (before 3.20.0) and still does.
export const START_TABS = [
  { id: 'dashboard', label: 'Overview' },
  { id: 'compose',   label: 'Compose',  tab: 'compose' },
  { id: 'outbox',    label: 'Outbox',   tab: 'outgoing' },
  { id: 'incoming',  label: 'Inbox',    tab: 'incoming' },
];
export const startChoice = v => (START_TABS.some(t => t.id === v) ? v : 'dashboard');

export function pathForTab(id) {
  const tab = TABS.find(t => t.id === id);
  return `${BASE}/${tab ? tab.slug : ''}`;
}

// The tab named by the current address; unknown addresses → default tab
export function tabFromLocation() {
  let path = window.location.pathname;
  const legacy = LEGACY_PREFIXES.find(p => path === p || path.startsWith(p + '/'));
  if (legacy) path = path.slice(legacy.length);
  else if (BASE && path.startsWith(BASE)) path = path.slice(BASE.length);
  const [slug, sub] = path.replace(/^\/+|\/+$/g, '').split('/');
  // /portal/logs is the Logs tab again since 3.14.0 (Control Panel → Logs
  // before); the old Control Panel address opens it too
  if (slug === 'control-panel' && sub === 'logs') return 'logs';
  // 3.49.18: Reminders moved from Users to Settings: older links open it there
  if ((slug === 'users' || slug === 'control-panel') && sub === 'reminders') {
    try { window.sessionStorage.setItem('faxPortal.settingsTab', 'reminders'); } catch { /* not kept */ }
    return 'settings';
  }
  if (slug === 'control-panel' && ['users', 'activity'].includes(sub)) return 'users';
  // 3.49.0: Console sections that became their own pages (links in emails
  // and older bookmarks still open them; the page tidies the address)
  if ((slug === 'console' || slug === 'control-panel') && MOVED_FROM_CONSOLE[sub]) return MOVED_FROM_CONSOLE[sub];
  const tab  = TABS.find(t => t.slug === slug) || TABS.find(t => t.id === OLD_SLUGS[slug]);
  return tab ? tab.id : DEFAULT_TAB;
}

// First tab this person can open (the default when theirs isn't allowed)
export function firstOpenTab(access) {
  return (TABS.find(t => canOpenTab(t.id, access)) || TABS[TABS.length - 1]).id;
}

export function labelForTab(id) {
  return TABS.find(t => t.id === id)?.label || 'Fax Portal';
}
